import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoSVG from '../../images/logo.svg';
import { db } from '../../firebase';
import { collection, getCountFromServer } from 'firebase/firestore';

/* ─── inline styles ─── */
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Sans:wght@300;400;500&display=swap');

  .dq-root *, .dq-root *::before, .dq-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .dq-root {
    --brown: #322920;
    --brown-mid: #4a3d31;
    --brown-light: #6b5a4a;
    --gold: #eccb4d;
    --gold-dark: #d4a820;
    --gold-pale: #f8eecc;
    --cream: #e7e6eb;
    --grey: #888481;
    --terra: #a5514f;
    --white: #ffffff;
    --serif: 'Playfair Display', serif;
    --sans: 'DM Sans', sans-serif;
    --poppins: 'Poppins', sans-serif;
    font-family: var(--sans);
    background: var(--cream);
    color: var(--brown);
    overflow-x: hidden;
    scroll-behavior: smooth;
  }

  /* NAV */
  .dq-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 48px;
    background: rgba(231,230,235,0.88);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(50,41,32,0.08);
    transition: padding .3s, box-shadow .3s;
  }
  .dq-nav.scrolled { padding: 12px 48px; box-shadow: 0 2px 20px rgba(50,41,32,0.08); }
  .dq-nav-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; cursor: pointer; }
  .dq-nav-logo img { width: 44px; height: auto; }
  .dq-nav-links { display: flex; align-items: center; gap: 32px; }
  .dq-nav-links a, .dq-nav-links button {
    font-size: 14px; font-weight: 400; color: var(--brown); text-decoration: none;
    opacity: .75; transition: opacity .2s; background: none; border: none; cursor: pointer; font-family: var(--sans);
  }
  .dq-nav-links a:hover, .dq-nav-links button:hover { opacity: 1; }
  .dq-nav-cta {
    background: var(--brown) !important; color: var(--gold) !important;
    opacity: 1 !important; padding: 9px 22px !important; border-radius: 100px !important;
    font-weight: 500 !important; transition: background .2s !important;
  }
  .dq-nav-cta:hover { background: var(--brown-mid) !important; }

  /* HERO */
  .dq-hero {
    min-height: 100vh; display: flex; flex-direction: column; align-items: center;
    justify-content: center; text-align: center; padding: 120px 24px 80px;
    position: relative; overflow: hidden;
    background:
      radial-gradient(ellipse at 15% 20%, rgba(236,203,77,0.22) 0%, transparent 55%),
      radial-gradient(ellipse at 88% 75%, rgba(165,81,79,0.14) 0%, transparent 50%),
      radial-gradient(ellipse at 55% 10%, rgba(236,203,77,0.12) 0%, transparent 45%),
      var(--cream);
  }
  .dq-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(50,41,32,0.06); border: 1px solid rgba(50,41,32,0.12);
    border-radius: 100px; padding: 6px 16px;
    font-size: 12px; font-weight: 500; letter-spacing: .06em; text-transform: uppercase;
    color: var(--brown-light); margin-bottom: 28px;
    animation: dqFadeUp .6s ease both;
  }
  .dq-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--gold-dark); display: inline-block; }
  .dq-hero h1 {
    font-family: var(--poppins); font-size: clamp(48px, 8vw, 96px);
    font-weight: 900; line-height: 1.0; color: var(--brown); max-width: 900px;
    animation: dqFadeUp .6s .1s ease both; opacity: 0; animation-fill-mode: both;
  }
  .dq-hero h1 em { font-style: normal; color: var(--gold-dark); position: relative; }
  .dq-hero h1 em::after {
    content: ''; position: absolute; bottom: 4px; left: 0; right: 0;
    height: 3px; background: var(--gold); border-radius: 2px;
  }
  .dq-hero-sub {
    font-size: clamp(16px, 2vw, 20px); font-weight: 300; color: var(--brown-light);
    max-width: 540px; line-height: 1.7; margin: 24px auto 0;
    animation: dqFadeUp .6s .2s ease both; opacity: 0; animation-fill-mode: both;
  }
  .dq-hero-actions {
    display: flex; gap: 14px; flex-wrap: wrap; justify-content: center;
    margin-top: 44px; animation: dqFadeUp .6s .3s ease both; opacity: 0; animation-fill-mode: both;
  }
  .dq-btn-primary {
    background: var(--brown); color: var(--gold);
    border: none; border-radius: 100px; padding: 15px 34px;
    font-family: var(--sans); font-size: 15px; font-weight: 500;
    cursor: pointer; text-decoration: none; transition: background .2s, transform .15s;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .dq-btn-primary:hover { background: var(--brown-mid); transform: translateY(-1px); }
  .dq-btn-outline {
    background: transparent; color: var(--brown);
    border: 1.5px solid rgba(50,41,32,0.3); border-radius: 100px; padding: 15px 34px;
    font-family: var(--sans); font-size: 15px; font-weight: 500;
    cursor: pointer; text-decoration: none; transition: border-color .2s, transform .15s;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .dq-btn-outline:hover { border-color: var(--brown); transform: translateY(-1px); }

  /* TICKET */
  .dq-hero-visual {
    margin-top: 72px; position: relative; display: flex; justify-content: center;
    animation: dqFadeUp .6s .4s ease both; opacity: 0; animation-fill-mode: both;
  }
  .dq-ticket {
    background: var(--white); border-radius: 24px; padding: 32px 40px;
    box-shadow: 0 24px 80px rgba(50,41,32,0.14), 0 4px 16px rgba(50,41,32,0.06);
    min-width: 300px; max-width: 340px; position: relative; overflow: hidden; text-align: left;
  }
  .dq-ticket::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: var(--gold); }
  .dq-ticket-shop { font-size: 11px; font-weight: 500; letter-spacing: .08em; text-transform: uppercase; color: var(--grey); margin-bottom: 8px; }
  .dq-ticket-name { font-family: var(--poppins); font-size: 22px; font-weight: 700; color: var(--brown); margin-bottom: 20px; }
  .dq-ticket-row { display: flex; align-items: flex-end; gap: 24px; }
  .dq-ticket-label { font-size: 11px; text-transform: uppercase; letter-spacing: .08em; color: var(--grey); }
  .dq-ticket-num { font-family: var(--poppins); font-size: 64px; font-weight: 900; line-height: 1; color: var(--gold-dark); }
  .dq-ticket-time { font-family: var(--poppins); font-size: 28px; font-weight: 700; color: var(--brown); margin-top: 4px; }
  .dq-ticket-slot { font-size: 13px; color: var(--brown-light); margin-top: 4px; }
  .dq-ticket-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3);
    color: #16a34a; font-size: 12px; font-weight: 500;
    padding: 5px 12px; border-radius: 100px; margin-top: 20px;
  }
  .dq-ticket-badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: #22c55e; }

  /* BUBBLES */
  .dq-bubble {
    position: absolute; background: var(--white); border-radius: 14px; padding: 12px 16px;
    box-shadow: 0 8px 24px rgba(50,41,32,0.12); font-size: 13px; font-weight: 500; white-space: nowrap;
    animation: dqFloat 4s ease-in-out infinite;
  }
  .dq-bubble-1 { top: 20px; right: -80px; color: var(--brown); animation-delay: 0s; }
  .dq-bubble-2 { bottom: 60px; left: -100px; color: var(--terra); animation-delay: 1.5s; }
  .dq-bubble-sub { font-size: 11px; font-weight: 400; color: var(--grey); margin-top: 2px; }

  /* STATS */
  .dq-stats {
    background: var(--brown); padding: 48px;
    display: flex; justify-content: center; gap: 80px; flex-wrap: wrap;
  }
  .dq-stat { text-align: center; }
  .dq-stat-num {
    font-family: var(--poppins); font-size: 44px; font-weight: 900; color: var(--gold); line-height: 1;
    transition: opacity .4s ease;
  }
  .dq-stat-num.loading { opacity: 0.35; animation: dqPulse 1.2s ease-in-out infinite; }
  @keyframes dqPulse { 0%,100% { opacity: 0.35; } 50% { opacity: 0.7; } }
  .dq-stat-label { font-size: 13px; color: rgba(255,255,255,0.5); margin-top: 6px; letter-spacing: .04em; }

  /* SECTIONS */
  .dq-section { padding: 96px 48px; }
  .dq-section-label {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 11px; font-weight: 500; letter-spacing: .1em; text-transform: uppercase;
    color: var(--gold-dark); margin-bottom: 16px;
  }
  .dq-section-label::before { content: ''; width: 24px; height: 2px; background: var(--gold); }
  .dq-section-title { font-family: var(--poppins); font-size: clamp(32px, 4vw, 48px); font-weight: 700; line-height: 1.15; color: var(--brown); max-width: 640px; }
  .dq-section-body { font-size: 16px; font-weight: 300; line-height: 1.75; color: var(--brown-light); max-width: 540px; margin-top: 16px; }

  /* HOW */
  .dq-how { background: var(--white); }
  .dq-how-inner { max-width: 1100px; margin: 0 auto; }
  .dq-how-header { text-align: center; margin-bottom: 72px; }
  .dq-steps { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 32px; }
  .dq-step {
    padding: 36px 32px; border-radius: 20px; border: 1px solid rgba(50,41,32,0.08);
    background: var(--cream); position: relative; transition: transform .2s, border-color .2s; opacity: 0;
  }
  .dq-step.visible { animation: dqFadeUp .6s ease forwards; }
  .dq-step:hover { transform: translateY(-4px); border-color: rgba(236,203,77,0.5); }
  .dq-step-num {
    font-family: var(--poppins); font-size: 13px; font-weight: 700; color: var(--gold-dark);
    margin-bottom: 20px; display: inline-block; background: rgba(236,203,77,0.15);
    border-radius: 100px; padding: 4px 12px;
  }
  .dq-step-icon { font-size: 32px; margin-bottom: 16px; }
  .dq-step h3 { font-family: var(--poppins); font-size: 20px; font-weight: 700; color: var(--brown); margin-bottom: 10px; }
  .dq-step p { font-size: 14px; font-weight: 300; line-height: 1.7; color: var(--brown-light); }

  /* FEATURES */
  .dq-features { background: var(--cream); }
  .dq-features-inner { max-width: 1100px; margin: 0 auto; }
  .dq-features-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 56px; }
  .dq-feat {
    background: var(--white); border-radius: 20px; padding: 36px;
    border: 1px solid rgba(50,41,32,0.06); transition: transform .2s; opacity: 0;
  }
  .dq-feat.visible { animation: dqFadeUp .6s ease forwards; }
  .dq-feat:hover { transform: translateY(-3px); }
  .dq-feat-hero { grid-column: span 2; background: var(--brown) !important; border-color: var(--brown) !important; }
  .dq-feat-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: center; }
  .dq-feat-icon {
    width: 48px; height: 48px; border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; margin-bottom: 20px; background: rgba(236,203,77,0.15);
  }
  .dq-feat h3 { font-family: var(--poppins); font-size: 20px; font-weight: 700; color: var(--brown); margin-bottom: 10px; }
  .dq-feat-hero h3 { color: var(--gold); }
  .dq-feat p { font-size: 14px; font-weight: 300; line-height: 1.7; color: var(--brown-light); }
  .dq-feat-hero p { color: rgba(255,255,255,0.6); }
  .dq-feat-badge {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; font-weight: 500; letter-spacing: .06em; text-transform: uppercase;
    color: var(--gold-dark); background: rgba(236,203,77,0.15);
    padding: 4px 12px; border-radius: 100px; margin-bottom: 16px;
  }
  .dq-wa-mockup {
    background: rgba(255,255,255,0.06); border-radius: 16px; padding: 20px;
    border: 1px solid rgba(255,255,255,0.1);
  }
  .dq-wa-header { font-size: 12px; color: rgba(255,255,255,0.4); margin-bottom: 14px; }
  .dq-wa-msg {
    background: rgba(255,255,255,0.1); border-radius: 12px 12px 12px 2px;
    padding: 12px 16px; font-size: 13px; color: rgba(255,255,255,0.85); line-height: 1.6;
  }
  .dq-wa-msg strong { color: var(--gold); }
  .dq-wa-time { font-size: 11px; color: rgba(255,255,255,0.3); margin-top: 6px; text-align: right; }

  /* BENEFITS TABS */
  .dq-benefits { background: var(--white); }
  .dq-benefits-inner { max-width: 1100px; margin: 0 auto; }

  .dq-tab-bar {
    display: flex; gap: 0; margin-top: 40px; margin-bottom: 48px;
    background: rgba(50,41,32,0.05); border-radius: 100px; padding: 5px;
    width: fit-content;
  }
  .dq-tab-btn {
    padding: 10px 28px; border-radius: 100px; border: none; cursor: pointer;
    font-family: var(--sans); font-size: 14px; font-weight: 500;
    background: transparent; color: var(--brown-light);
    transition: all .2s; white-space: nowrap;
  }
  .dq-tab-btn.active {
    background: var(--brown); color: var(--gold);
    box-shadow: 0 2px 12px rgba(50,41,32,0.2);
  }

  .dq-benefits-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
  }
  .dq-benefit-card {
    padding: 28px 28px; border-radius: 20px;
    border: 1px solid rgba(50,41,32,0.08); background: var(--cream);
    display: flex; gap: 20px; align-items: flex-start;
    transition: transform .2s, border-color .2s;
    animation: dqFadeUp .4s ease both;
  }
  .dq-benefit-card:hover { transform: translateY(-3px); border-color: rgba(236,203,77,0.5); }
  .dq-benefit-icon {
    width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0;
    background: rgba(236,203,77,0.15); border: 1px solid rgba(236,203,77,0.25);
    display: flex; align-items: center; justify-content: center; font-size: 24px;
  }
  .dq-benefit-card h3 {
    font-family: var(--poppins); font-size: 17px; font-weight: 700;
    color: var(--brown); margin-bottom: 8px; line-height: 1.2;
  }
  .dq-benefit-card p {
    font-size: 14px; font-weight: 300; line-height: 1.7; color: var(--brown-light);
  }

  /* FOR WHO */
  .dq-forwho { background: var(--cream); }
  .dq-forwho-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
  .dq-forwho-list { list-style: none; margin-top: 32px; display: flex; flex-direction: column; gap: 16px; }
  .dq-forwho-list li {
    display: flex; align-items: flex-start; gap: 16px; padding: 20px 24px;
    background: var(--white); border-radius: 14px; border: 1px solid rgba(50,41,32,0.06);
    font-size: 15px; font-weight: 300; color: var(--brown-light); line-height: 1.5;
    transition: border-color .2s; opacity: 0;
  }
  .dq-forwho-list li.visible { animation: dqFadeUp .6s ease forwards; }
  .dq-forwho-list li:hover { border-color: rgba(236,203,77,0.5); }
  .dq-forwho-list li strong { display: block; font-weight: 500; color: var(--brown); font-size: 15px; margin-bottom: 2px; }
  .dq-check {
    width: 28px; height: 28px; border-radius: 50%; background: var(--gold);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; font-size: 13px;
  }
  .dq-barber-card {
    background: var(--gold-pale); border-radius: 24px; padding: 40px;
    border: 1px solid rgba(236,203,77,0.4); text-align: center; position: relative; overflow: hidden;
  }
  .dq-barber-card::before {
    content: ''; position: absolute; top: -60px; right: -60px;
    width: 200px; height: 200px; border-radius: 50%; background: rgba(236,203,77,0.2);
  }
  .dq-scissors { font-size: 72px; margin-bottom: 20px; }
  .dq-barber-card h3 { font-family: var(--poppins); font-size: 24px; font-weight: 700; color: var(--brown); margin-bottom: 8px; }
  .dq-barber-card p { font-size: 14px; color: var(--brown-light); font-weight: 300; line-height: 1.6; }
  .dq-barber-stats { display: flex; gap: 20px; margin-top: 28px; justify-content: center; }
  .dq-barber-stat { text-align: center; }
  .dq-barber-stat-n { font-family: var(--poppins); font-size: 28px; font-weight: 700; color: var(--brown); }
  .dq-barber-stat-l { font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: var(--brown-light); margin-top: 2px; }

  /* PRICING */
  .dq-pricing { background: var(--cream); }
  .dq-pricing-inner { max-width: 900px; margin: 0 auto; }
  .dq-pricing-header { text-align: center; margin-bottom: 56px; }
  .dq-pricing-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; }
  .dq-plan {
    background: var(--white); border-radius: 20px; padding: 36px;
    border: 1px solid rgba(50,41,32,0.08); position: relative; overflow: hidden;
    transition: transform .2s; opacity: 0;
  }
  .dq-plan.visible { animation: dqFadeUp .6s ease forwards; }
  .dq-plan:hover { transform: translateY(-4px); }
  .dq-plan-featured { background: var(--brown) !important; border-color: var(--brown) !important; }
  .dq-plan-featured::before {
    content: 'Most Popular'; position: absolute; top: 20px; right: -28px;
    background: var(--gold); color: var(--brown); font-size: 10px; font-weight: 700;
    letter-spacing: .08em; text-transform: uppercase; padding: 4px 40px; transform: rotate(45deg);
  }
  .dq-plan-name { font-size: 12px; font-weight: 500; letter-spacing: .1em; text-transform: uppercase; color: var(--grey); margin-bottom: 16px; }
  .dq-plan-featured .dq-plan-name { color: rgba(255,255,255,0.5); }
  .dq-plan-price { font-family: var(--poppins); font-size: 44px; font-weight: 900; color: var(--brown); line-height: 1; }
  .dq-plan-featured .dq-plan-price { color: var(--gold); }
  .dq-plan-period { font-size: 13px; color: var(--grey); margin-top: 4px; }
  .dq-plan-featured .dq-plan-period { color: rgba(255,255,255,0.4); }
  .dq-plan-divider { height: 1px; background: rgba(50,41,32,0.08); margin: 24px 0; }
  .dq-plan-featured .dq-plan-divider { background: rgba(255,255,255,0.1); }
  .dq-plan-features { list-style: none; display: flex; flex-direction: column; gap: 10px; }
  .dq-plan-features li { font-size: 14px; color: var(--brown-light); display: flex; align-items: center; gap: 10px; }
  .dq-plan-featured .dq-plan-features li { color: rgba(255,255,255,0.7); }
  .dq-plan-features li::before { content: '✓'; color: var(--gold-dark); font-weight: 700; flex-shrink: 0; }
  .dq-plan-featured .dq-plan-features li::before { color: var(--gold); }
  .dq-plan-btn {
    display: block; width: 100%; margin-top: 28px; padding: 13px; border-radius: 100px;
    text-align: center; font-family: var(--sans); font-size: 14px; font-weight: 500;
    text-decoration: none; cursor: pointer; transition: .2s;
    border: 1.5px solid rgba(50,41,32,0.2); color: var(--brown); background: transparent;
  }
  .dq-plan-btn:hover { background: var(--cream); }
  .dq-plan-featured .dq-plan-btn { background: var(--gold); border-color: var(--gold); color: var(--brown); }
  .dq-plan-featured .dq-plan-btn:hover { background: var(--gold-dark); }

  /* CTA */
  .dq-cta {
    background: var(--brown); padding: 96px 48px; text-align: center; position: relative; overflow: hidden;
  }
  .dq-cta::before {
    content: ''; position: absolute; inset: 0;
    background:
      radial-gradient(ellipse at 20% 50%, rgba(236,203,77,0.12) 0%, transparent 60%),
      radial-gradient(ellipse at 80% 50%, rgba(165,81,79,0.10) 0%, transparent 55%);
  }
  .dq-cta > * { position: relative; }
  .dq-cta h2 { font-family: var(--poppins); font-size: clamp(32px, 4vw, 52px); font-weight: 900; color: var(--gold); margin-bottom: 16px; }
  .dq-cta p { font-size: 16px; font-weight: 300; color: rgba(255,255,255,0.6); max-width: 480px; margin: 0 auto 40px; line-height: 1.7; }
  .dq-cta .dq-btn-primary { background: var(--gold); color: var(--brown); font-size: 16px; padding: 16px 40px; }
  .dq-cta .dq-btn-primary:hover { background: var(--gold-dark); }

  /* FOOTER */
  .dq-footer {
    background: #808080; padding: 48px;
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 24px;
  }
  .dq-footer-logo { display: flex; align-items: center; gap: 10px; }
  .dq-footer-logo img { width: 100px; height: auto; }
  .dq-footer-tagline { font-size: 12px; color: rgba(255,255,255,0.3); margin-top: 4px; }
  .dq-footer-links { display: flex; gap: 24px; }
  .dq-footer-links button {
    font-size: 13px; color: rgba(255,255,255,0.4); text-decoration: none;
    transition: color .2s; background: none; border: none; cursor: pointer; font-family: var(--sans);
  }
  .dq-footer-links button:hover { color: var(--gold); }
  .dq-footer-copy { font-size: 12px; color: rgba(255,255,255,0.25); }

  /* KEYFRAMES */
  @keyframes dqFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes dqFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }

  /* RESPONSIVE */
  @media (max-width: 768px) {
    .dq-nav { padding: 16px 24px !important; }
    .dq-nav-links { display: none; }
    .dq-section { padding: 64px 24px; }
    .dq-stats { padding: 40px 24px; gap: 40px; }
    .dq-features-grid { grid-template-columns: 1fr; }
    .dq-feat-hero { grid-column: span 1; }
    .dq-feat-inner { grid-template-columns: 1fr; }
    .dq-benefits-grid { grid-template-columns: 1fr; }
    .dq-tab-bar { width: 100%; justify-content: stretch; }
    .dq-tab-btn { flex: 1; text-align: center; padding: 10px 12px; font-size: 13px; }
    .dq-forwho-inner { grid-template-columns: 1fr; }
    .dq-pricing-grid { grid-template-columns: 1fr; }
    .dq-bubble-1, .dq-bubble-2 { display: none; }
    .dq-footer { flex-direction: column; padding: 40px 24px; }
  }
  .dq-hero-logo img { width: 200px; height: auto; }
`;

// ── benefits data ─────────────────────────────────────────────────────────────
const BENEFITS = {
  customer: [
    { icon: '📲', title: 'Walk-In via QR Scan',          description: 'Walk straight into the shop, scan the QR code to join the queue instantly, and see your queue number and estimated wait time.' },
    { icon: '📅', title: 'Online Booking',                description: 'Book your slot in advance through a fast and simple online system — anytime, anywhere.' },
    { icon: '👍', title: 'Most Recommended Barber',       description: 'Choose the most trusted barber based on real reviews and top ratings from actual customers.' },
    { icon: '⏱️', title: 'Estimated Wait Time',           description: 'Know how long the wait is before you even arrive, so you can plan your time more wisely.' },
  ],
  owner: [
    { icon: '📊', title: 'Real-Time Dashboard',           description: 'Monitor queues, appointments and barber status live — no refreshing needed.' },
    { icon: '👥', title: 'Manage Your Barber Team',       description: 'Add new barbers with an invite code and track their daily performance at a glance.' },
    { icon: '🔲', title: 'QR Queue System',               description: 'Customers scan the QR to join the queue themselves — no front desk, no bottlenecks.' },
    { icon: '📈', title: 'Reports & Analytics',           description: 'View total bookings, walk-ins and the popularity of each service you offer.' },
  ],
  barber: [
    { icon: '✂️', title: 'Personal Dashboard',            description: 'Manage your own queue and appointments easily — everything in one clean view.' },
    { icon: '🔔', title: 'Availability Toggle',           description: 'Switch your status between available and unavailable at any time with a single tap.' },
    { icon: '🗓️', title: 'Daily Schedule',                description: "See all your today's appointments and walk-ins in one organised list." },
    { icon: '⭐', title: 'Build Your Reputation',         description: 'Collect reviews and grow your name within the local barber community.' },
  ],
};

const TAB_META = [
  { key: 'customer', label: '👤 Customers' },
  { key: 'owner',    label: '🏪 Shop Owners' },
  { key: 'barber',   label: '✂️ Barbers' },
];

function formatCount(n) {
  if (n === null) return '…';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k+`;
  return `${n}+`;
}

export default function Landing() {
  const navigate = useNavigate();
  const navRef   = useRef(null);
  const [activeTab, setActiveTab] = useState('customer');

  // ── live stats ──
  const [stats, setStats] = useState({ shops: null, bookings: null });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [shopsSnap, bookingsSnap] = await Promise.all([
          getCountFromServer(collection(db, 'shops')),
          getCountFromServer(collection(db, 'bookings')),
        ]);
        setStats({ shops: shopsSnap.data().count, bookings: bookingsSnap.data().count });
      } catch (err) {
        console.error('Stats fetch failed:', err);
        setStats({ shops: 0, bookings: 0 });
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const id = 'dq-global-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = globalStyles;
      document.head.appendChild(el);
    }
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  useEffect(() => {
    const handler = () => navRef.current?.classList.toggle('scrolled', window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll('.dq-step, .dq-feat, .dq-plan, .dq-forwho-list li');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const statsRows = [
    { value: formatCount(stats.shops),    label: 'Barber Shops',        loading: stats.shops === null },
    { value: formatCount(stats.bookings), label: 'Queue Tickets Issued', loading: stats.bookings === null },
    { value: '40%',                       label: 'Less Walk-offs',       loading: false },
    { value: '4.9★',                      label: 'Customer Rating',      loading: false },
  ];

  return (
    <div className="dq-root">

      {/* NAV */}
      <nav className="dq-nav" ref={navRef}>
        <div className="dq-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img src={LogoSVG} alt="DigitalQ" />
        </div>
        <div className="dq-nav-links">
          <button onClick={() => scrollTo('dq-how')}>How it works</button>
          <button onClick={() => scrollTo('dq-features')}>Features</button>
          <button onClick={() => scrollTo('dq-benefits')}>Benefits</button>
          <button onClick={() => scrollTo('dq-pricing')}>Pricing</button>
          <button className="dq-nav-cta" onClick={() => navigate('/role')}>Get Started Free</button>
        </div>
      </nav>

      {/* HERO */}
      <section className="dq-hero">
        <div className="dq-hero-logo"><img src={LogoSVG} alt="DigitalQ" /></div>
        <div className="dq-eyebrow"><span className="dq-eyebrow-dot"></span>Malaysia's #1 Barber Queue System</div>
        <h1>No More <em>Waiting</em><br />in Line</h1>
        <p className="dq-hero-sub">DigitalQ lets your customers join the queue from their phone and get notified when it's their turn. Less waiting. More cutting.</p>
        <div className="dq-hero-actions">
          <button className="dq-btn-primary" onClick={() => navigate('/role')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></svg>
            Start for Free
          </button>
          <button className="dq-btn-outline" onClick={() => scrollTo('dq-how')}>See how it works</button>
        </div>
        <div className="dq-hero-visual">
          <div className="dq-ticket">
            <div className="dq-ticket-shop">DigitalQ — Subang Jaya</div>
            <div className="dq-ticket-name">Ahmad bin Razif</div>
            <div className="dq-ticket-row">
              <div>
                <div className="dq-ticket-label">Queue No.</div>
                <div className="dq-ticket-num">07</div>
              </div>
              <div style={{ paddingBottom: 10 }}>
                <div className="dq-ticket-label">Time Slot</div>
                <div className="dq-ticket-time">2:30 PM</div>
                <div className="dq-ticket-slot">with Barber Faiz</div>
              </div>
            </div>
            <div className="dq-ticket-badge">You're next — please make your way up!</div>
          </div>
          <div className="dq-bubble dq-bubble-1">📱 WhatsApp sent!<div className="dq-bubble-sub">You're 2nd in queue</div></div>
          <div className="dq-bubble dq-bubble-2">✂️ 3 barbers available<div className="dq-bubble-sub">Avg wait: 20 min</div></div>
        </div>
      </section>

      {/* STATS */}
      <div className="dq-stats">
        {statsRows.map(({ value, label, loading }) => (
          <div className="dq-stat" key={label}>
            <div className={`dq-stat-num${loading ? ' loading' : ''}`}>{value}</div>
            <div className="dq-stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* HOW IT WORKS */}
      <section className="dq-section dq-how" id="dq-how">
        <div className="dq-how-inner">
          <div className="dq-how-header">
            <div className="dq-section-label">How it works</div>
            <h2 className="dq-section-title" style={{ margin: '0 auto', textAlign: 'center' }}>Up and running in minutes</h2>
          </div>
          <div className="dq-steps">
            {[
              { n:'01', icon:'🏪', title:'Register your shop',     body:'Sign up and add your barbers, services, and operating hours. Takes less than 5 minutes.' },
              { n:'02', icon:'📲', title:'Customers scan & join',  body:'Customers scan your QR code or visit your link. They choose a barber and join the queue instantly — no app needed.' },
              { n:'03', icon:'🔔', title:'Auto WhatsApp alert',    body:"When they're next, DigitalQ sends them a WhatsApp message automatically. No more shouting names." },
              { n:'04', icon:'✂️', title:'You focus on cutting',   body:"Manage your queue live from any device. Mark customers as served, see who's next, and track your daily stats." },
            ].map(s => (
              <div className="dq-step" key={s.n}>
                <div className="dq-step-num">{s.n}</div>
                <div className="dq-step-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="dq-section dq-features" id="dq-features">
        <div className="dq-features-inner">
          <div className="dq-section-label">Features</div>
          <h2 className="dq-section-title">Everything your barbershop needs</h2>
          <div className="dq-features-grid">
            <div className="dq-feat dq-feat-hero">
              <div className="dq-feat-inner">
                <div>
                  <div className="dq-feat-badge">★ Most loved</div>
                  <h3>WhatsApp Notifications</h3>
                  <p>Customers leave the shop, grab a meal, and come back exactly when it's their turn. Automatic messages sent by our system — no manual texting needed.</p>
                </div>
                <div className="dq-wa-mockup">
                  <div className="dq-wa-header">📱 WhatsApp · DigitalQ</div>
                  <div className="dq-wa-msg">
                    ⏰ <strong>Bersiap sedia, Ahmad!</strong><br /><br />
                    Giliran anda adalah yang <strong>seterusnya</strong> — sila beratur dekat kerusi ya 🪑<br /><br />
                    📋 Queue No. <strong>#07</strong> · Barber <strong>Faiz</strong>
                  </div>
                  <div className="dq-wa-time">2:28 PM ✓✓</div>
                </div>
              </div>
            </div>
            {[
              { icon:'📊', title:'Live Queue Dashboard',      body:"See all your barbers' queues in real-time. Mark customers as serving, done, or no-show with a single tap." },
              { icon:'📅', title:'Walk-in & Appointments',   body:'Handle both walk-in customers and pre-booked appointments in one system. Full calendar view with available slots.' },
              { icon:'⭐', title:'Reviews & Ratings',        body:'After each visit, customers can rate their barber. Build your reputation and attract new customers automatically.' },
              { icon:'🪪', title:'Barber Profiles & Portfolio', body:'Each barber gets their own profile with photos of their work, specialties, and availability. Customers pick who they trust.' },
            ].map(f => (
              <div className="dq-feat" key={f.title}>
                <div className="dq-feat-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS — tabbed by role */}
      <section className="dq-section dq-benefits" id="dq-benefits">
        <div className="dq-benefits-inner">
          <div className="dq-section-label">Benefits</div>
          <h2 className="dq-section-title">Built for everyone</h2>
          <p className="dq-section-body">Whether you're a customer, shop owner, or barber — DigitalQ delivers a better experience for all.</p>

          {/* Tab bar */}
          <div className="dq-tab-bar">
            {TAB_META.map(t => (
              <button
                key={t.key}
                className={`dq-tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Benefit cards */}
          <div className="dq-benefits-grid" key={activeTab}>
            {BENEFITS[activeTab].map((b, i) => (
              <div
                className="dq-benefit-card"
                key={b.title}
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                <div className="dq-benefit-icon">{b.icon}</div>
                <div>
                  <h3>{b.title}</h3>
                  <p>{b.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR WHO */}
      <section className="dq-section dq-forwho">
        <div className="dq-forwho-inner">
          <div>
            <div className="dq-section-label">Built for barbers</div>
            <h2 className="dq-section-title">Works for every kind of barbershop</h2>
            <p className="dq-section-body">Whether you're a solo barber or running multiple chairs, DigitalQ scales with your business.</p>
            <ul className="dq-forwho-list">
              {[
                { title:'Solo barbers',          body:'Let customers join remotely while you focus — no receptionist needed.' },
                { title:'Multi-chair shops',     body:'Each barber has their own queue. Customers pick their favourite or take the next available.' },
                { title:'High-volume shops',     body:'On busy weekends, DigitalQ keeps things organised so no one walks off frustrated.' },
                { title:'Appointment-based shops', body:'Full booking calendar with time slots, services, and customer history.' },
              ].map(i => (
                <li key={i.title}>
                  <div className="dq-check">✓</div>
                  <div><strong>{i.title}</strong>{i.body}</div>
                </li>
              ))}
            </ul>
          </div>
          <div className="dq-barber-card">
            <div className="dq-scissors">✂️</div>
            <h3>Trusted by barbers<br />across Malaysia</h3>
            <p>From Kuala Lumpur to Johor Bahru, barbershops are reducing wait times and keeping customers happy.</p>
            <div className="dq-barber-stats">
              <div className="dq-barber-stat"><div className="dq-barber-stat-n">40%</div><div className="dq-barber-stat-l">Less walk-offs</div></div>
              <div className="dq-barber-stat"><div className="dq-barber-stat-n">2×</div><div className="dq-barber-stat-l">More returning customers</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="dq-section dq-pricing" id="dq-pricing">
        <div className="dq-pricing-inner">
          <div className="dq-pricing-header">
            <div className="dq-section-label" style={{ justifyContent: 'center' }}>Pricing</div>
            <h2 className="dq-section-title" style={{ margin: '0 auto', textAlign: 'center' }}>Simple, honest pricing</h2>
            <p style={{ fontSize: 15, color: 'var(--brown-light)', fontWeight: 300, marginTop: 12, lineHeight: 1.6 }}>Start free. Upgrade when you're ready. No contracts.</p>
          </div>
          <div className="dq-pricing-grid">
            {[
              { name:'Free',   price:'RM0',  period:'Forever free',      features:['1 barber','Walk-in queue','Basic dashboard','Customer queue ticket','QR code for shop'],                                              cta:'Get Started',       featured:false },
              { name:'Pro',    price:'RM49', period:'per month per shop', features:['Unlimited barbers','Walk-in + appointments','WhatsApp notifications','Reviews & ratings','Barber portfolios','Analytics dashboard'], cta:'Start 14-day trial', featured:true  },
              { name:'Master', price:'RM99', period:'per month',          features:['Multiple branches','All Pro features','Priority WhatsApp quota','Custom branding','Priority support','Dedicated account manager'],   cta:'Contact Sales',     featured:false },
            ].map(p => (
              <div className={`dq-plan${p.featured ? ' dq-plan-featured' : ''}`} key={p.name}>
                <div className="dq-plan-name">{p.name}</div>
                <div className="dq-plan-price">{p.price}</div>
                <div className="dq-plan-period">{p.period}</div>
                <div className="dq-plan-divider"></div>
                <ul className="dq-plan-features">{p.features.map(f => <li key={f}>{f}</li>)}</ul>
                <button className="dq-plan-btn" onClick={() => navigate('/signin')}>{p.cta}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="dq-cta">
        <h2>Ready to cut the wait?</h2>
        <p>Join hundreds of Malaysian barbers already using DigitalQ to serve more customers and earn more.</p>
        <button className="dq-btn-primary" onClick={() => navigate('/role')}>
          Get Started Free Today
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>
      </section>

      {/* FOOTER */}
      <footer className="dq-footer">
        <div className="dq-footer-logo">
          <img src={LogoSVG} alt="DigitalQ" />
          <div>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 700, color: '#eccb4d' }}>DigitalQ</div>
            <div className="dq-footer-tagline">Smart Queue for Malaysian Barbers</div>
          </div>
        </div>
        <div className="dq-footer-links">
          <button onClick={() => scrollTo('dq-how')}>How it works</button>
          <button onClick={() => scrollTo('dq-features')}>Features</button>
          <button onClick={() => scrollTo('dq-benefits')}>Benefits</button>
          <button onClick={() => scrollTo('dq-pricing')}>Pricing</button>
          <button onClick={() => navigate('/signin')}>Login</button>
        </div>
        <div className="dq-footer-copy">© 2025 DigitalQ. Made in Malaysia 🇲🇾</div>
      </footer>
    </div>
  );
}